import OrganisationApi from '@packages/organisations/api';

import { download } from '@utils/file';

import { Investor, VisibilityGroup, InvestorAdviserRelationship, ProviderInvestor } from '@/models';
import Collection from '@/models/Collection';

export class InvestorApi extends OrganisationApi {
    static resource = 'investors';

    get endpoints() {
        return {
            ...super.endpoints,
            postClassification: this.postClassification.bind(this),
            applyForInvestmentAccount: this.applyForInvestmentAccount.bind(this),
            getAppropriateness: this.getAppropriateness.bind(this),
            postAppropriateness: this.postAppropriateness.bind(this),
            downloadAppropriateness: this.downloadAppropriateness.bind(this),
            getVisibilityGroups: this.getVisibilityGroups.bind(this),
            getApplicationForm: this.getApplicationForm.bind(this),
            getAdviserRelationship: this.getAdviserRelationship.bind(this),
            postAdviserRelationship: this.postAdviserRelationship.bind(this),
            putAdviserRelationship: this.putAdviserRelationship.bind(this),
            patchAdviserRelationship: this.patchAdviserRelationship.bind(this),
            removeAdviserRelationship: this.removeAdviserRelationship.bind(this),
            postDriEnrollment: this.postDriEnrollment.bind(this),
            providers: this.providers.bind(this),
            products: this.products.bind(this),
            changeEmailAddress: this.changeEmailAddress.bind(this),
            withdrawCash: this.withdrawCash.bind(this),
            getProviderRelationships: this.getProviderRelationships.bind(this),
            getProviderRelationship: this.getProviderRelationship.bind(this),
            postProviderRelationship: this.postProviderRelationship.bind(this),
            putProviderRelationship: this.putProviderRelationship.bind(this),
            patchProviderRelationship: this.patchProviderRelationship.bind(this),
            removeProviderRelationship: this.removeProviderRelationship.bind(this),
            requestProviderLOA: this.requestProviderLOA.bind(this),
            downloadProviderLOA: this.downloadProviderLOA.bind(this),
            reinvestHoldings: this.reinvestHoldings.bind(this),
            getAdditionalNeeds: this.getAdditionalNeeds.bind(this),
            getAdditionalNeed: this.getAdditionalNeed.bind(this),
            postAdditionalNeed: this.postAdditionalNeed.bind(this),
            patchAdditionalNeed: this.patchAdditionalNeed.bind(this),
            removeAdditionalNeed: this.removeAdditionalNeed.bind(this)
        };
    }

    _clearCollection(type) {
        type = type || this.model.name || null;
        if (!type) return;

        Collection.delete(collection => collection.type.includes(type));
        Collection.delete(collection => collection.type.includes('Client'));
    }

    _filterResponseData(data) {
        data = super._filterResponseData(data);

        if (data && data.id && data.adviser_accounts) {
            data.adviser_accounts = data.adviser_accounts.map(aa => {
                aa.investor_id = data.id;
                aa.adviser_id = aa.adviser.id;
                return aa;
            });
        }

        return data;
    }

    async postClassification(investorId, payload = {}) {
        const response = await OrganisationApi.api.post(`${this.base_url}/${investorId}/classifications`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection();
            this._clearCollection('Client');
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async applyForInvestmentAccount(investorId, payload = {}) {
        const response = await OrganisationApi.api.post(`${this.base_url}/${investorId}/apply`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection();
            this._clearCollection('Client');
            Investor.delete(investorId);
            InvestorAdviserRelationship.delete(item => item.investor_id === investorId);
        });
    }

    async getAppropriateness(investorId, config = {}) {
        const response = await OrganisationApi.api.get(`${this.base_url}/${investorId}/appropriateness`, {}, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            if (Array.isArray(response.data.questions)) {
                response.data.questions = response.data.questions.map(question => {
                    if (Array.isArray(question.answers)) {
                        question.answers = question.answers.map(answer => {
                            if (
                                !answer.text ||
                                (typeof answer.text === 'object' && Object.keys(answer.text).length === 0)
                            ) {
                                answer.text = null;
                            }
                            return answer;
                        });
                    }
                    return question;
                });
            }
        });
    }

    async postAppropriateness(investorId, questions = []) {
        const response = await OrganisationApi.api.post(`${this.base_url}/${investorId}/appropriateness`, {
            questions
        });

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async downloadAppropriateness(investorId, blank = false) {
        return await OrganisationApi.api.get(
            `${this.base_url}/${investorId}/appropriateness/download`,
            blank ? { blank: true } : {},
            {
                headers: {
                    Accept: 'application/pdf'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );
    }

    async getVisibilityGroups(investorId) {
        const response = await OrganisationApi.api.get(`${this.base_url}/${investorId}/visibility-groups`);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._insertModels(response.data, VisibilityGroup);
        });
    }

    async getApplicationForm(investorId, adviserId) {
        let url = `${this.base_url}`;

        if (investorId) {
            url += `/${investorId}`;
        }

        url += '/application-form';

        return await OrganisationApi.api.get(
            url,
            { adviser_id: adviserId },
            {
                headers: {
                    Accept: 'application/pdf'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );
    }

    async providers(offset = null, limit = null, sort_by = null, filters = {}, config = { base_url: null }) {
        let extract = OrganisationApi.extractFilter(filters, 'investor_id');

        if (!extract.value) {
            throw new Error(`investor_id is required when requesting providers for an investor.`);
        }

        let url = `${config.base_url || this.base_url}`;

        if (extract.value) {
            url += `/${extract.value}`;
        }

        url += '/providers';

        return this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });
    }

    async products(offset = null, limit = null, sort_by = null, filters = {}, config = { base_url: null }) {
        let extract = OrganisationApi.extractFilter(filters, 'investor_id');

        if (!extract.value) {
            throw new Error(`investor_id is required when requesting products for an investor.`);
        }

        let url = `${config.base_url || this.base_url}`;

        if (extract.value) {
            url += `/${extract.value}`;
        }

        url += '/products';

        return this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });
    }

    async getAdviserRelationship(investor_id, adviser_id, params = {}, options = {}) {
        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async postAdviserRelationship(payload = {}) {
        const { investor_id, adviser_id } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id on POST investor/adviser relationship', payload);
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on POST investor/adviser relationship', payload);
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });

            const investor = Investor.find(investor_id);

            if (investor) {
                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: [...investor.adviser_accounts, response.data]
                    }
                });
            }
        });
    }

    async putAdviserRelationship(investor_id, adviser_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PUT investor/adviser relationship', payload);
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on PUT investor/adviser relationship', payload);
        }

        const response = await OrganisationApi.api.put(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });

            const investor = Investor.find(investor_id);

            if (investor && Array.isArray(investor.adviser_accounts)) {
                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: investor.adviser_accounts.map(aa => {
                            if (aa.adviser_id === adviser_id) {
                                return response.data;
                            }
                            return aa;
                        })
                    }
                });
            }
        });
    }

    async patchAdviserRelationship(investor_id, adviser_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PATCH investor/adviser relationship', payload);
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on PATCH investor/adviser relationship', payload);
        }

        const response = await OrganisationApi.api.patch(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });

            const investor = Investor.find(investor_id);

            if (investor && Array.isArray(investor.adviser_accounts)) {
                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: investor.adviser_accounts.map(aa => {
                            if (aa.adviser_id === adviser_id) {
                                return { ...aa, ...payload };
                            }
                            return aa;
                        })
                    }
                });
            }
        });
    }

    async removeAdviserRelationship(investor_id, adviser_id) {
        if (!investor_id) {
            throw new Error('Missing investor_id on DELETE investor/adviser relationship');
        }
        if (!adviser_id) {
            throw new Error('Missing adviser_id on DELETE investor/adviser relationship');
        }

        const response = await OrganisationApi.api.remove(
            `${this.base_url}/${investor_id}/adviser-relationship/${adviser_id}`
        );

        return this._respond(response, () => {
            this._clearCollection('InvestorAdviserRelationship');
            InvestorAdviserRelationship.delete(
                item => item.investor_id === investor_id && item.adviser_id === adviser_id
            );

            const investor = Investor.find(investor_id);

            if (investor) {
                Investor.update({
                    where: investor_id,
                    data: {
                        adviser_accounts: investor.adviser_accounts.filter(aa => aa.adviser_id !== adviser_id)
                    }
                });
            }
        });
    }

    async postDriEnrollment(payload = {}) {
        const { investor_id, product_id } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id on POST DRI enrollment', payload);
        }
        if (!product_id) {
            throw new Error('Missing product_id on POST DRI enrollment', payload);
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/products/${product_id}/dri`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async changeEmailAddress(investor_id, email_address = null) {
        if (!investor_id) {
            throw new Error('Missing investor_id on change email address');
        }
        if (!email_address) {
            throw new Error('Missing email_address on change email address');
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/change-email`, {
            email_address
        });

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            const investor = Investor.find(investor_id);

            if (investor) {
                Investor.update({
                    where: investor_id,
                    data: { email: response.data.email }
                });
            }
        });
    }

    async withdrawCash(investor_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on cash withdrawal');
        }
        if (!payload.bank_account_id) {
            throw new Error('Missing bank_account_id on cash withdrawal');
        }
        if (
            !payload.cash_withdrawals ||
            !Array.isArray(payload.cash_withdrawals) ||
            payload.cash_withdrawals.length === 0
        ) {
            throw new Error('Missing cash_withdrawals on cash withdrawal');
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/cash-withdrawal`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    _filterProviderRelationshipResponseData(data) {
        if (!data) {
            return data;
        }

        const setId = item => ({
            id: `${item.investor?.id || item.investor_id || ProviderInvestor.default_investor_id}_${
                item.provider?.id || item.provider_id || ProviderInvestor.default_provider_id
            }`,
            ...item
        });

        if (Array.isArray(data)) {
            return data.map(item => setId(item));
        }

        return setId(data);
    }

    async getProviderRelationships(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let extract = OrganisationApi.extractFilter(filters, 'investor_id');

        if (!extract.value) {
            throw new Error(`investor_id is required when requesting provider relationships for an investor.`);
        }

        let url = `${this.base_url}/${extract.value}/provider-relationships`;

        const response = await this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });

        if (Array.isArray(response.data)) {
            response.data = this._filterProviderRelationshipResponseData(response.data);
        }

        return response;
    }

    async getProviderRelationship(investor_id, provider_id, params = {}, options = {}) {
        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async postProviderRelationship(payload = {}) {
        const { investor_id, provider_id } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id on POST investor/provider relationship', payload);
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on POST investor/provider relationship', payload);
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/provider-relationships`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async putProviderRelationship(investor_id, provider_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PUT investor/provider relationship', payload);
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on PUT investor/provider relationship', payload);
        }

        const response = await OrganisationApi.api.put(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async patchProviderRelationship(investor_id, provider_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PATCH investor/provider relationship', payload);
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on PATCH investor/provider relationship', payload);
        }

        const response = await OrganisationApi.api.patch(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.insertOrUpdate({ data: { ...response.data, $responseFormat: 20 } });
        });
    }

    async removeProviderRelationship(investor_id, provider_id) {
        if (!investor_id) {
            throw new Error('Missing investor_id on DELETE investor/provider relationship');
        }
        if (!provider_id) {
            throw new Error('Missing provider_id on DELETE investor/provider relationship');
        }

        const response = await OrganisationApi.api.remove(
            `${this.base_url}/${investor_id}/provider-relationships/${provider_id}`
        );

        return this._respond(response, () => {
            this._clearCollection('ProviderInvestor');
            ProviderInvestor.delete(item => item.investor_id === investor_id && item.provider_id === provider_id);
        });
    }

    async requestProviderLOA(investor_id, provider_ids) {
        if (!investor_id) {
            throw new Error('Missing investor_id on request provider LOA');
        }

        if (!provider_ids) {
            throw new Error('Missing provider_ids on request provider LOA');
        }

        const response = await OrganisationApi.api.post(
            `${this.base_url}/${investor_id}/provider-relationships/request-loa`,
            {
                provider_ids
            }
        );

        return this._respond(response, () => {
            response.data = this._filterProviderRelationshipResponseData(response.data);
        });
    }

    async downloadProviderLOA(investor_id, provider_ids, downloadFile = false) {
        if (!investor_id) {
            throw new Error('Missing investor_id on download provider LOA');
        }

        if (!provider_ids) {
            throw new Error('Missing provider_ids on download provider LOA');
        }

        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/provider-relationships/request-loa/download`,
            {
                provider_ids
            },
            {
                headers: {
                    Accept: 'application/pdf, application/zip'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );

        if (downloadFile) {
            const filename = response.request.getResponseHeader('x-filename');
            const name = filename || 'LOA';

            const reader = new FileReader();

            reader.onloadend = async () => {
                await download(reader.result, name);
            };

            reader.readAsDataURL(response.data);
        }

        return response;
    }

    async reinvestHoldings(payload = {}) {
        const { investor_id, holding_ids } = payload;

        if (!investor_id) {
            throw new Error('Missing investor_id while attempting to re-invest holdings', payload);
        }
        if (!holding_ids) {
            throw new Error('Missing holding_ids while attempting to re-invest holdings', payload);
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/holdings/reinvest`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getAdditionalNeeds(investorId, config = {}) {
        const response = await OrganisationApi.api.get(`${this.base_url}/${investorId}/additional-needs`, {}, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getAdditionalNeed(investor_id, additional_need_id, params = {}, options = {}) {
        const response = await OrganisationApi.api.get(
            `${this.base_url}/${investor_id}/additional-needs/${additional_need_id}`,
            params,
            options
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async postAdditionalNeed(investor_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on POST additional need', payload);
        }

        const response = await OrganisationApi.api.post(`${this.base_url}/${investor_id}/additional-needs`, payload);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            const investor = Investor.find(investor_id);

            if (investor) {
                let additional_needs = investor.additional_needs || [];

                const existingIndex = additional_needs.findIndex(
                    need => need.id === response.data.id || need.type === response.data.type
                );

                if (existingIndex > -1) {
                    additional_needs[`${existingIndex}`] = response.data;
                } else {
                    additional_needs.push(response.data);
                }

                Investor.update({
                    where: investor_id,
                    data: { additional_needs, has_additional_needs: additional_needs.length > 0 }
                });
            }
        });
    }

    async patchAdditionalNeed(investor_id, additional_need_id, payload = {}) {
        if (!investor_id) {
            throw new Error('Missing investor_id on PATCH additional need', payload);
        }
        if (!additional_need_id) {
            throw new Error('Missing additional_need_id on PATCH additional need', payload);
        }

        const response = await OrganisationApi.api.patch(
            `${this.base_url}/${investor_id}/additional-needs/${additional_need_id}`,
            payload
        );

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            const investor = Investor.find(investor_id);

            if (investor) {
                let additional_needs = investor.additional_needs || [];

                const existingIndex = additional_needs.findIndex(
                    need => need.id === response.data.id || need.type === response.data.type
                );

                if (existingIndex > -1) {
                    additional_needs[`${existingIndex}`] = response.data;
                } else {
                    additional_needs.push(response.data);
                }

                Investor.update({
                    where: investor_id,
                    data: { additional_needs, has_additional_needs: additional_needs.length > 0 }
                });
            }
        });
    }

    async removeAdditionalNeed(investor_id, additional_need_id) {
        if (!investor_id) {
            throw new Error('Missing investor_id on DELETE additional need');
        }
        if (!additional_need_id) {
            throw new Error('Missing additional_need_id on DELETE additional need');
        }

        const response = await OrganisationApi.api.remove(
            `${this.base_url}/${investor_id}/additional-needs/${additional_need_id}`
        );

        return this._respond(response, () => {
            const investor = Investor.find(investor_id);

            if (investor) {
                let additional_needs = investor.additional_needs || [];
                additional_needs = additional_needs.filter(need => need.id !== additional_need_id);

                Investor.update({
                    where: investor_id,
                    data: { additional_needs, has_additional_needs: additional_needs.length > 0 }
                });
            }
        });
    }
}

export default InvestorApi;
